import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, Input, OnInit, Output, SimpleChanges, OnChanges } from '@angular/core';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { InputType } from '../../types/input-type.type';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './input.component.html'
})
export class InputComponent implements OnInit, OnChanges {
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input() type!: InputType;
  @Input() isDisabled: boolean = false;
  @Input() inputId: string = '';
  @Input() isReadonly: boolean = false;
  @Input() control!: FormControl;
  @Input() isBigger: boolean = false;

  @Output() valueChange = new EventEmitter<{ id: string, value: string }>();

  isFocused: boolean = false;
  isPasswordVisible: boolean = false;
  inputType: string = 'password';
  inputValue = new FormControl('',Validators.required);

  readonly size = computed(() => this.isBigger ? 'w-[320px]' : 'w-[295px]');
  ngOnInit(): void {


    this.getValidators();
    this.updateDisabledState();

    this.control.valueChanges.subscribe(() => {
      this.control.markAsTouched();
      this.control.markAsDirty();
      this.control.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isDisabled']) {
      this.updateDisabledState();
    }
  }

  private updateDisabledState() {
    if (this.control) {
      if (this.isDisabled) {
        this.control.disable({ emitEvent: false });
      } else {
        this.control.enable({ emitEvent: false });
      }
    }
  }


  getValidators(){
    switch(this.type){
      case 'email':
        this.control.setValidators([Validators.required, Validators.email]);
        break;
      case 'password':
        this.control.setValidators([Validators.required, (control) => this.passwordValidator(control) ]);
        break;
      case 'text':
        this.control.setValidators([Validators.required,Validators.minLength(3)]) ;
        break;
    }

    this.control.updateValueAndValidity();
  }

  getErrorMessage(): string {
    if (this.control.hasError('email')) {
      return 'Introduce una dirección de correo válida';
    }
    if (this.control.hasError('passwordInvalid')) {
      return 'Mínimo 8 caracteres, con una mayúscula, una minúscula y un número';
    }
    return 'Este campo es requerido';
  }

  togglePasswordVisibility(){
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  clearInput(){
    this.control.reset();
  }

  getInputValue():string{
    return this.inputValue.value??'';
  }

  markAsTouched() {
    this.control.markAsTouched();
    this.control.updateValueAndValidity();
  }





  private passwordValidator(control: AbstractControl): ValidationErrors | null {
    const password: string = control.value ?? '';

    if (!password) return null;

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasMinLength = password.length >= 8;

    if (!hasMinLength || !hasUpperCase || !hasLowerCase || !hasNumber) {
      return { passwordInvalid: true };
    }

    return null;
  }

  get dynamicClasses() {
  return {
    'w-[392px]': this.type === 'password' || this.type === 'email',
    [`${this.size()}`]: this.type === 'text',
    'border-[#0A4E8F] focus:border-[#0A4E8F] border-[2px]': this.type === 'text' || !this.control.invalid || !this.control.touched && !this.isReadonly,
    'border-[#FF3030] focus:border-[#FF3030] border-[2px]': this.control.invalid && this.control.touched && this.type !== 'text' && !this.isReadonly,
    'pl-12': this.type === 'text',
    'placeholder-[#C2C2C2] bg-transparent': this.isDisabled,
    'bg-[#E7EDF466] border-none placeholder:font-semibold placeholder:text-[#272B34]': this.isReadonly && (this.type === 'password' || this.type === 'email')
  };
}

get inputClasses() {
  return {
    ...(this.dynamicClasses || {}),
    'pl-[52px] pr-[52px] py-[11px]': this.isBigger,
    'px-[16px] py-[12px]': !this.isBigger
  };
}
}
